import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { env } from '../../../environments/environment';
import { DialogService } from '../../dialog/dialog.service';
import { FlagService } from '../../flag/flag.service';
import { Answer, Inspection } from '../../shared/interfaces';
import { MergeAnswersService } from '../../shared/merge.answers.service';
import { ViewerWindowService } from '../../shared/viewer.window.service';
import { AddFlagComponent } from '../add.flag/add.flag.component';
import { AnswerStatus } from '../answer.status';
import { EditFlagComponent } from '../edit.flag/edit.flag.component';
import { PictureViewerComponent } from '../picture-viewer/picture-viewer.component';
import { PictureService } from '../picture.service';
import { InspectionFilters, ReviewNavigationAction } from '../review.navigation/review.navigation.component';
import { UserService } from '../../user/user.service';
import { InspectionShareGalleryModalComponent } from '../inspection-share-gallery-modal/inspection-share-gallery-modal.component';

@Component({
  selector: 'app-inspection-gallery',
  templateUrl: './inspection-gallery.component.html',
  styleUrls: ['./inspection-gallery.component.scss'],
})
export class InspectionGalleryComponent implements OnChanges, OnDestroy, OnInit {
  @Input() answers: Answer[];
  @Input() filters: InspectionFilters;
  @Input() inspection: Inspection;
  @Input() navigationComponent: any;

  filterSetPromise: Promise<any>;
  filterSetResolve: any;

  @ViewChild('editFlagModal', { static: false }) editFlagModal: EditFlagComponent;
  @ViewChild('addFlagModal', { static: false }) addFlagModal: AddFlagComponent;
  @ViewChild('info', { static: false }) info: ModalDirective;
  @ViewChild('pictureViewer', { static: false }) pictureViewer: PictureViewerComponent;
  @ViewChild('shareGalleryLinkModal', { static: false }) shareGalleryLinkModal!: InspectionShareGalleryModalComponent;

  private _fullscreen = false;
  public get fullscreen() {
    return this._fullscreen;
  }
  public set fullscreen(value) {
    this._fullscreen = value;
    this.updateQueryParams();
  }

  showUnpinnedFlags = false;

  private _currentPicture: any;
  public get currentPicture(): any {
    return this._currentPicture;
  }
  public set currentPicture(value: any) {
    this._currentPicture = value;
    this.popup?.selectImage(value?.id);
  }

  showLabels = false;
  gridSize: 'large' | 'small' = 'large';

  pageSizeOptions = [10, 50, 100];
  pageSize = 10;

  downloading = false;
  includeMarkup = false;
  flagPlaceholder;

  popupActive = false;
  initialPictureId: number;

  subscriptions: any[] = [];

  _currentPage = 1;

  rotating = false;

  private pageLoadSubject = new Subject<number>();

  get currentPage(): number {
    return this._currentPage;
  }

  set currentPage(page: number) {
    if (page !== this._currentPage) {
      this._currentPage = page;
      this.pageLoadSubject.next(page);
    }
  }

  allPictures: any[] = [];
  items: any[];
  allFlags: any[] = [];
  totalCount = 0;

  private activeImageControllers: AbortController[] = [];
  private activeBlobUrls: string[] = [];
  private batchLoadToken = 0; // Used to cancel batches if needed

  constructor(
    private mergeAnswersService: MergeAnswersService,
    public pictureService: PictureService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: DialogService,
    private toastr: ToastrService,
    private flagService: FlagService,
    private popup: ViewerWindowService,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterSetPromise = new Promise(resolve => (this.filterSetResolve = resolve));
  }

  async ngOnInit() {
    const settings = (await this.userService.getSetting('gallery-view')) ?? {};
    console.log('settings:', settings);

    if (!settings.pageSize) {
      settings['pageSize'] = 10;
    }

    this.pageSize = settings.pageSize;
    this.cdr.detectChanges();
    this.subscriptions = [
      this.route.queryParams.subscribe(params => {
        this.currentPage = Number(params['page']) || 1;
        this.pageSize = Number(params['pageSize']) || this.pageSize;
        this.initialPictureId = Number(params['pictureId']) || null;
      }),

      this.pageLoadSubject.pipe(debounceTime(100)).subscribe(() => {
        this.loadCurrentPageImages();
        window.scrollTo(0, 0);
      }),

      this.popup.getWindowStatus().subscribe(isOpen => {
        this.popupActive = isOpen;
      }),

      this.popup.getMovedFlag().subscribe(event => {
        const flag = this.currentPicture.answer.flags.find(f => f.id === event.flag.id);
        if (flag) {
          this.moveFlag({ flag, x: event.x, y: event.y });
        }
      }),

      this.popup.getUpdatedAnswer().subscribe(event => {
        this.currentPicture.answer = event.answer;
        this.currentPicture.updatedAt = new Date();
        this.loadImage(this.currentPicture);
      }),

      this.popup.getNavigatedPicture().subscribe(event => {
        if (event === 'prev') {
          this.prev();
        } else if (event === 'next') {
          this.next();
        }
      }),

      this.popup.getPinnedFlag().subscribe(event => {
        const { flag, x, y } = event;
        const localFlag = this.currentPicture.answer.flags.find(f => f.id === flag.id);
        this.pinFlag(localFlag, x, y);
      }),
    ];
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['answers'] || changes['inspection']) {
      if (this.answers && this.inspection) {
        await this.filterSetPromise;
        await this.setUpTree();
      }
    }
    if (changes['filters'] && changes['filters'].previousValue) {
      this.currentPage = 1;
      this.loadCurrentPageImages();
      this.filterSetResolve();
    }
  }

  ngOnDestroy() {
    this.pageLoadSubject.complete();
    this.abortAllImageRequests();
    this.revokeAllBlobUrls();
    this.popup.close();
    this.subscriptions.forEach(sub => {
      sub?.unsubscribe?.();
    });
  }

  async setUpTree() {
    this.allFlags = [];
    this.answers.forEach(answer => {
      this.allFlags.push(...(answer.flags || []));
      answer.pictures.forEach(picture => {
        picture.selected = false;
        picture.answer = answer;
      });
    });

    this.items = [...(await this.mergeAnswersService.loadItems(this.inspection, this.answers))];
    this.mergeAnswersService.mergeAnswers(this.items);

    this.allPictures = (this.answers || []).flatMap(a => a.pictures || []);

    this.allPictures.forEach(picture => {
      const item =
        picture.answer &&
        picture.answer.question &&
        picture.answer.question.section &&
        picture.answer.question.section.item &&
        picture.answer.question.section.item.label;
      const section =
        picture.answer &&
        picture.answer.question &&
        picture.answer.question.section &&
        picture.answer.question.section.name;
      const question = picture.answer && picture.answer.question && picture.answer.question.text;
      picture.label = `${item} / ${section} / ${question}`;
      picture.createdDate = picture.answer?.createdAt;

      // format policy number - item - section - sequential number for the question
      picture.downloadName = `${this.inspection.policyNumber} - ${item} - ${section} - ${picture.id}.jpg`;
    });

    this.totalCount = this.allPictures.length;
    this.pageLoadSubject.next(this.currentPage);
  }

  get filteredPictures() {
    const allIds = new Set();

    const filteredPictures = [...this.allPictures].filter(p => {
      if (allIds.has(p.id)) {
        return false;
      } else {
        allIds.add(p.id);
      }

      if (this.filters) {
        const item = p.answer?.question?.section?.item?.label;
        const section = p.answer?.question?.section?.id;
        const comments = p.answer?.comments?.length ?? 0;
        const question = p.answer?.question;
        const flags = this.allFlags.filter(f => f.pictureId === p.id).length;

        if (this.filters.flags && this.filters.flags !== 'all') {
          if (
            !p.answer.flags.some(flag => this.filters.flags !== 'all' && this.filters.flags.includes(flag.subtypeId))
          ) {
            return false;
          }
        }

        if (!this.filters.withoutFlags && !flags) {
          return false;
        }

        if (!this.filters.withoutComments && !comments) {
          return false;
        }

        if (!this.filters.accepted) {
          const isAccepted = p.answer.status === AnswerStatus.ACCEPTED;
          if (isAccepted) {
            return false;
          }
        }

        if (!this.filters.unreviewed) {
          const isNotReviewed = p.answer.status === AnswerStatus.NOT_REVIEWED;
          if (isNotReviewed) {
            return false;
          }
        }

        if (!this.filters.rejected) {
          const isRejected = p.answer.status === AnswerStatus.REJECTED;
          if (isRejected) {
            return false;
          }
        }

        const isMandatory = p.answer.question.mandatoryReview;
        const isScored = p.answer.flaggingScore;

        if (this.filters.questions === 'mandatory' && !isMandatory) {
          return false;
        }

        if (this.filters.questions === 'scored' && !isScored) {
          return false;
        }

        if (this.filters.questions === 'mandatoryOrScored' && !(isScored || isMandatory)) {
          return false;
        }

        if (this.filters.questions === 'autoScored') {
          if (!isScored) {
            return false;
          }
          if (!p.answer.flags.find(x => x.systemFlag)) {
            return false;
          }
        }

        if (!this.filters.items.includes(item)) {
          return false;
        }

        if (!this.filters.sections.includes(section)) {
          return false;
        }

        const questionTagIds = question.questionTags.map(x => x.id);
        const hasQuestionTags = this.filters.questionTags.length > 0;
        const noQuestionTag = this.filters.questionTags.includes(0);
        const questionHasTags = question.questionTags.length > 0;
        const hasMoreThanOneTag = this.filters.questionTags.length > 1;
        const noMatchingTags = !this.filters.questionTags.some(element => questionTagIds.includes(element));

        if (!hasQuestionTags) {
          return false;
        }

        if (noQuestionTag) {
          if (hasMoreThanOneTag) {
            return !(questionTagIds.length > 0 && noMatchingTags);
          } else {
            return !questionHasTags;
          }
        } else {
          return !(questionTagIds.length === 0 || (questionTagIds.length > 0 && noMatchingTags));
        }
      }
      return true;
    });

    if (this.filters) {
      if (this.filters.sorting === 'followConfigurationOrder') {
        filteredPictures.sort((a, b) => {
          return a.answer.questionId - b.answer.questionId;
        });
      } else {
        filteredPictures.sort((a, b) => {
          const diff = new Date(a.answer.createdAt).getTime() - new Date(b.answer.createdAt).getTime();
          return this.filters.sorting === 'asc' ? diff : -diff;
        });
      }
    }

    return filteredPictures;
  }

  get totalPages(): number {
    return Math.ceil(this.filteredPictures.length / this.pageSize) || 1;
  }

  get pagedPictures(): any[] {
    // Handle picture deeplink - only run first time
    if (this.initialPictureId) {
      const initialPicture = this.allPictures.find(p => p.id === this.initialPictureId);

      if (initialPicture) {
        // Picture exists
        // Check if the picture is already in the filtered list
        const isInFilteredList = this.filteredPictures.some(p => p.id === initialPicture.id);
        if (!isInFilteredList) {
          // If not, clear the filters
          this.navigationComponent.applyShortcut('all-questions');
        } else {
          // If it is, set the current picture to the initial one
          this.currentPicture = initialPicture;
          this.initialPictureId = null; // Reset after using
          this.fullscreen = true; // Open the picture in fullscreen
        }
      }
    }

    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredPictures.slice(start, start + this.pageSize);
  }

  get selectedPictures(): any[] {
    return this.allPictures.filter(p => p.selected);
  }

  uncheckAll() {
    this.selectedPictures.forEach(pic => {
      pic.selected = false;
    });
  }
  checkAll() {
    this.pagedPictures.forEach(pic => {
      pic.selected = true;
    });
  }

  setPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
    this.updateQueryParams();
  }

  setPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.savePreference();
    this.pageLoadSubject.next(this.currentPage);
    this.updateQueryParams();
  }

  private updateQueryParams() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: this.currentPage,
        pageSize: this.pageSize,
        pictureId: this.fullscreen ? this.currentPicture?.id : undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  embeddedUrl(picture) {
    const updatedAt = picture.updatedAt;
    const timestamp = new Date(updatedAt).getTime();
    return `${env.APIUrl}/image/embedded/${picture.id}?t=${timestamp}`;
  }

  private async loadImagesWithConcurrency(pictures: any[], concurrency: number = 10) {
    const myToken = ++this.batchLoadToken;
    let index = 0;

    return new Promise<void>(resolve => {
      let active = 0;

      const next = () => {
        if (myToken !== this.batchLoadToken) return resolve();

        while (active < concurrency && index < pictures.length) {
          const picture = pictures[index++];
          active++;
          this.loadImage(picture).finally(() => {
            active--;
            next();
          });
        }

        if (index >= pictures.length && active === 0) {
          resolve();
        }
      };

      next();
    });
  }

  async loadCurrentPageImages() {
    this.abortAllImageRequests();
    this.revokeAllBlobUrls();
    if (!this.pagedPictures) return;
    await this.loadImagesWithConcurrency(this.pagedPictures);
  }

  private loadImage(picture: any) {
    const url = this.embeddedUrl(picture);
    picture.url = url;

    if (!url) {
      picture.imageUrl = null;
      picture.loading = false;
      return;
    }

    picture.loading = true;

    const controller = new AbortController();
    this.activeImageControllers.push(controller);

    return fetch(url, { signal: controller.signal })
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.blob();
      })
      .then(async blob => {
        const blobUrl = URL.createObjectURL(blob);
        picture.imageUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
        this.activeBlobUrls.push(blobUrl);
        picture.loading = false;
      })
      .catch(() => {
        picture.imageUrl = null;
        picture.loading = false;
      });
  }

  private abortAllImageRequests() {
    this.batchLoadToken++; // Invalidate any running batch
    this.activeImageControllers.forEach(ctrl => ctrl.abort());
    this.activeImageControllers = [];
  }

  private revokeAllBlobUrls() {
    this.activeBlobUrls.forEach(url => URL.revokeObjectURL(url));
    this.activeBlobUrls = [];
    this.allPictures.forEach(p => {
      p.imageUrl = null;
      p.loading = true;
    });
  }

  async rotate(picture: any, degree: number = 90) {
    picture.loading = true;
    const savedPicture = await this.pictureService.rotate({ id: picture.id, degree });
    // Sync client state with server response to avoid stale Location/key and orientation mismatch
    picture.updatedAt = savedPicture.updatedAt;
    if (savedPicture?.Location) {
      picture.Location = savedPicture.Location;
    }
    if (savedPicture?.key) {
      picture.key = savedPicture.key;
    }
    if (savedPicture?.versions) {
      picture.versions = savedPicture.versions;
    }
    picture.degree = 0;
    await this.loadImage(picture);
    this.pictureViewer?.resetImage();
  }

  async rotateSelected(degree: number = 90) {
    this.rotating = true;
    try {
      const cannotRotateCount = this.selectedPictures.filter(p => !this.canRotate(p)).length;

      let goAhead = false;
      if (cannotRotateCount) {
        goAhead = await new Promise<boolean>(resolve => {
          this.dialog.confirm().show({
            header: 'Rotate selected pictures',
            message: `Some of the selected images include markup/flags. We can only rotate images which are markup/flag-free. Images with markup/flags won't be rotated.`,
            confirmed: () => resolve(true),
            canceled: () => resolve(false),
          });
        });
      } else {
        goAhead = await new Promise<boolean>(resolve => {
          this.dialog.confirm().show({
            header: 'Rotate selected pictures',
            message: `Are you sure you want to rotate the selected pictures?`,
            confirmed: () => resolve(true),
            canceled: () => resolve(false),
          });
        });
      }

      if (!goAhead) {
        return;
      }

      const pictures = this.selectedPictures.filter(picture => this.canRotate(picture));

      pictures.forEach(async picture => {
        picture.loading = true;
      });

      const ids = pictures.map(picture => picture.id);
      const rotatedPictures = await this.pictureService.bulkRotate({ ids, counterClockwise: degree < 0 });

      // Sync returned metadata to local pictures to avoid stale Location/key and orientation mismatch
      const rotatedById = new Map<number, any>((rotatedPictures || []).map((p: any) => [p.id, p]));
      const rotatedIds = new Set<number>(Array.from(rotatedById.keys()));

      // Update selected/local objects
      pictures.forEach(picture => {
        const updated = rotatedById.get(picture.id);
        if (updated) {
          picture.updatedAt = updated.updatedAt ?? new Date();
          picture.Location = updated.Location ?? picture.Location;
          picture.key = updated.key ?? picture.key;
          picture.versions = updated.versions ?? picture.versions;
          picture.degree = 0;
        } else {
          // Fallback for any not returned
          picture.updatedAt = new Date();
          picture.degree = 0;
        }
      });

      // If the current picture was rotated, sync its fields and refresh the viewer
      if (this.currentPicture && rotatedIds.has(this.currentPicture.id)) {
        const updated = rotatedById.get(this.currentPicture.id);
        if (updated) {
          this.currentPicture.updatedAt = updated.updatedAt ?? new Date();
          this.currentPicture.Location = updated.Location ?? this.currentPicture.Location;
          this.currentPicture.key = updated.key ?? this.currentPicture.key;
          this.currentPicture.versions = updated.versions ?? this.currentPicture.versions;
          this.currentPicture.degree = 0;
        } else {
          this.currentPicture.updatedAt = new Date();
          this.currentPicture.degree = 0;
        }
        this.pictureViewer?.resetImage();
      }

      await this.loadImagesWithConcurrency(pictures);
    } catch (e) {
      this.toastr.error('Error rotating images');
    }
    this.rotating = false;
  }

  async downloadPicture(url, filename = 'image.jpg') {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(blob);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      return console.error('Error downloading image:', error);
    }
  }

  async downloadSelected() {
    this.downloading = true;
    if (this.selectedPictures.length === 1 && !this.includeMarkup) {
      await this.downloadPicture(this.selectedPictures[0].Location, this.selectedPictures[0].downloadName);
      this.downloading = false;
    } else {
      this.dialog.confirm().show({
        header: 'Download selected pictures',
        message: `The file will be generated and you'll receive a download link in your e-mail when it's ready`,
        confirmed: async () => {
          this.toastr.success('Your file is being generated');
          this.downloading = false;
          await this.pictureService.download(
            this.selectedPictures.map(pic => pic.id),
            this.includeMarkup,
          );
        },
        canceled: () => (this.downloading = false),
      });
    }
  }

  toggleSelection(picture: any): void {
    picture.selected = !picture.selected;
  }

  canRotate(picture: any) {
    if (picture.loading) {
      return false;
    }

    const flags = picture.answer.flags.filter(flag => {
      if (flag.picture) {
        return flag.picture.key === picture.key;
      }
    });
    return !(flags.length > 0 || picture.markup.length);
  }

  getLinkParams(picture) {
    if (picture) {
      const item = this.items.indexOf(picture.answer.question.section.item);
      const section = this.items[item].sections.indexOf(picture.answer.question.section);
      const question = this.items[item].sections[section].questions.indexOf(picture.answer.question);
      return {
        item,
        section,
        question,
        right: 'question',
        currentImage: picture.id,
      };
    }
  }

  gotoQuestion(picture) {
    this.router.navigate([`/inspection/review/main/`, this.inspection.id], {
      queryParamsHandling: 'merge',
      queryParams: this.getLinkParams(picture),
    });
  }

  open(picture: any) {
    this.currentPicture = picture;
    this.updateQueryParams();

    this.fullscreen = true;
  }

  exitFullscreen() {
    this.fullscreen = false;
  }

  unpinnedFlagCount(picture: any) {
    return picture?.answer?.flags?.filter(flag => !flag.pictureId)?.length || 0;
  }

  get hasNext() {
    const currentIndex = this.filteredPictures.findIndex(p => p.id === this.currentPicture.id);
    return currentIndex < this.filteredPictures.length - 1;
  }

  next() {
    const currentIndex = this.filteredPictures.findIndex(p => p.id === this.currentPicture.id);
    if (this.hasNext) {
      this.currentPicture = this.filteredPictures[currentIndex + 1];
      // set page to the next page if needed
      if (currentIndex + 1 >= this.currentPage * this.pageSize) {
        this.currentPage++;
      }
      this.closeUnpinnedFlags();
    }
  }

  get hasPrev() {
    const currentIndex = this.filteredPictures.findIndex(p => p.id === this.currentPicture.id);
    return currentIndex > 0;
  }

  prev() {
    const currentIndex = this.filteredPictures.findIndex(p => p.id === this.currentPicture.id);
    if (this.hasPrev) {
      this.currentPicture = this.filteredPictures[currentIndex - 1];
      // set page to the prev page if needed
      if (currentIndex - 1 < (this.currentPage - 1) * this.pageSize) {
        this.currentPage--;
      }
      this.closeUnpinnedFlags();
    }
  }

  getFlags(picture: any) {
    return (
      picture?.answer?.flags.filter(flag => {
        if (flag.picture) {
          return flag.picture.key === picture.key;
        }
      }) || []
    );
  }

  get totalItems() {
    // This fixes an issue when the page is loading and we try to set the page from URL
    return this.filteredPictures.length || Number.MAX_SAFE_INTEGER;
  }

  updatePicture(picture?: any) {
    if (!picture) {
      picture = this.currentPicture;
    }
    this.currentPicture.updatedAt = new Date();
    this.loadImage(this.currentPicture);
  }

  editFlag({ flag, clientX, clientY }) {
    const answer = this.answers.find(a => a.id === flag.answerId);
    this.editFlagModal.show({
      flag,
      answer,
      coords: { clientX, clientY },
      changed: newFlag => {
        this.currentPicture.answer.flags = this.currentPicture.answer.flags.map(f => {
          if (f.id === newFlag.id) {
            return newFlag;
          }
          return f;
        });
        this.toastr.success('Flag Saved');
        this.updatePicture();
      },
      deleted: () => {
        this.currentPicture.answer.flags.splice(
          this.currentPicture.answer.flags.findIndex(f => f.id === flag.id),
          1,
        );

        this.toastr.success('Flag Removed');
        this.updatePicture();
      },
    });
  }

  async moveFlag({ flag, x, y }) {
    await this.flagService.edit(flag.id, { ...flag, x, y });
    flag.x = x;
    flag.y = y;

    this.toastr.success('Flag Saved');
    this.updatePicture();
  }

  addFlag(coords) {
    this.flagPlaceholder = coords;
    this.addFlagModal.show({
      answer: this.currentPicture.answer,
      picture: this.currentPicture,
      coords,
      confirmed: () => {
        this.toastr.success('Flag Saved');
        this.updatePicture();
        this.flagPlaceholder = undefined;
        // this.updateCurrentFlags();
      },
      canceled: () => {
        this.flagPlaceholder = undefined;
      },
    });
  }

  showInfo() {
    this.info.show();
  }

  closeInfo() {
    this.info.hide();
  }

  closePopup() {}

  async togglePopup() {
    if (this.popup.isOpen()) {
      this.popup.close();
      this.fullscreen = false;
    } else {
      await this.popup.open();
      this.popup.setPictures(this.filteredPictures);

      if (!this.currentPicture) {
        this.currentPicture = this.pagedPictures[0];
      }

      this.popup.selectImage(this.currentPicture.id);
    }
  }

  async pinFlag(flag, x?, y?) {
    const coord = this.pictureViewer?.imgSize;

    x = x || coord[0] / 2;
    y = y || coord[1] / 2;

    await this.flagService.edit(flag.id, {
      ...flag,
      picture: { id: this.currentPicture?.id },
      pictureId: this.currentPicture?.id,
      y,
      x,
    });

    flag.picture = { ...this.currentPicture };
    flag.pictureId = this.currentPicture.id;
    flag.x = x;
    flag.y = y;
    delete flag.picture.answer;

    this.currentPicture.updatedAt = new Date();
    this.updatePicture(this.currentPicture);
    this.popup.setPictures(this.filteredPictures);
    this.popup.selectImage(this.currentPicture.id);

    if (!this.unpinnedFlagCount(this.currentPicture)) {
      this.closeUnpinnedFlags();
    }
  }

  toggleUnpinnedFlags() {
    this.showUnpinnedFlags = !this.showUnpinnedFlags;
    this.pictureViewer?.resetImage();
  }

  closeUnpinnedFlags() {
    if (this.showUnpinnedFlags) {
      this.toggleUnpinnedFlags();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.target === document.body) {
      switch (event.key) {
        case 'ArrowLeft':
          this.prev();
          break;
        case 'ArrowRight':
          this.next();
          break;
        case 'Escape':
          if (!this.popupActive) {
            this.fullscreen = false;
          }
      }
    }
  }

  onNavAction(action: ReviewNavigationAction) {
    switch (action) {
      case ReviewNavigationAction.DUAL_MONITOR:
        this.togglePopup();
        break;
    }
  }

  async savePreference() {
    await this.userService.saveSetting('gallery-view', { pageSize: this.pageSize }).toPromise();
  }

  isGallerySharingEnabled() {
    return this.inspection.galleryWithMarkupToken || this.inspection.galleryWithoutMarkupToken;
  }

  openShareGalleryLinkModal() {
    this.shareGalleryLinkModal.openModal();
  }
}
